import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, TextFormat, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './user-purchased-plans.reducer';

export const UserPurchasedPlans = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, ITEMS_PER_PAGE, 'id'), location.search)
  );

  const userPurchasedPlansList = useAppSelector(state => state.userPurchasedPlans.entities);
  const loading = useAppSelector(state => state.userPurchasedPlans.loading);
  const totalItems = useAppSelector(state => state.userPurchasedPlans.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (location.search !== endURL) {
      navigate(`${location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [location.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="user-purchased-plans-heading" data-cy="UserPurchasedPlansHeading">
        <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.home.title">User Purchased Plans</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/user-purchased-plans/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.home.createLabel">Create new User Purchased Plans</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {userPurchasedPlansList && userPurchasedPlansList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('planStartDate')}>
                  <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.planStartDate">Plan Start Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('planStartDate')} />
                </th>
                <th className="hand" onClick={sort('planEndDate')}>
                  <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.planEndDate">Plan End Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('planEndDate')} />
                </th>

                <th>
                  <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.user">User</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.productDeliveryStatus">
                    Product Delivery Status
                  </Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.transactionDetails">Transaction Details</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {userPurchasedPlansList.map((userPurchasedPlans, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/user-purchased-plans/${userPurchasedPlans.id}`} color="link" size="sm">
                      {userPurchasedPlans.id}
                    </Button>
                  </td>
                  <td>
                    {userPurchasedPlans.planStartDate ? (
                      <TextFormat type="date" value={userPurchasedPlans.planStartDate} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {userPurchasedPlans.planEndDate ? (
                      <TextFormat type="date" value={userPurchasedPlans.planEndDate} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{userPurchasedPlans.user ? userPurchasedPlans.user.login : ''}</td>
                  <td>
                    {userPurchasedPlans.productDeliveryStatus ? (
                      <Link to={`/product-delivery-status/${userPurchasedPlans.productDeliveryStatus.id}`}>
                        {userPurchasedPlans.productDeliveryStatus.status}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {userPurchasedPlans.transactionDetails ? (
                      <Link to={`/transaction-details/${userPurchasedPlans.transactionDetails.id}`}>
                        {userPurchasedPlans.transactionDetails.paymentTransactionId}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/user-purchased-plans/${userPurchasedPlans.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/user-purchased-plans/${userPurchasedPlans.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/user-purchased-plans/${userPurchasedPlans.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.home.notFound">No User Purchased Plans found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={userPurchasedPlansList && userPurchasedPlansList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default UserPurchasedPlans;
